import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import type { TranslationKey } from "@/constants/i18n";
import { api, errorMessage, ifMatch } from "@/services/api";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";
import { EditDialog, FormPanel } from "@/components/resource-page";
import { AddButton, ListSection } from "@/components/ui/list-section";
import { Loading } from "@/components/ui/loading";
import { SkeletonLines } from "@/components/ui/skeleton";
import { useRecordParam } from "@/hooks/use-record-param";
import { listResource } from "@/utils/geo";
import { cn } from "@/utils/utils";

/**
 * Configurable option lists: one table of (kind, text) rows behind every
 * picker the design calls a "reason" — amendment reasons, note reasons, and
 * the complimentary sign-off names. Each screen picks which kinds it shows and
 * what its dialog calls things.
 */

export type ReasonKind =
  | "card_replacement"
  | "blacklist"
  | "cancellation"
  | "endorsement"
  | "complimentary_endorsed_by"
  | "complimentary_approved_by";

export interface Reason {
  id: number;
  kind: ReasonKind;
  reason: string;
}

const LIST_PAGE = 10;

/** What one group's dialog says. */
interface DialogCopy {
  title: TranslationKey;
  subtitle?: TranslationKey;
  fieldLabel: TranslationKey;
  multiline?: boolean;
}

interface GroupDef {
  kind: ReasonKind;
  heading: TranslationKey;
  addLabel: TranslationKey;
  copy: DialogCopy;
}

const AMENDMENT_OPTIONS: {
  value: ReasonKind;
  label: TranslationKey;
  heading: TranslationKey;
}[] = [
  {
    value: "card_replacement",
    label: "reasons.cardReplacement",
    heading: "reasons.cardReplacementHeading",
  },
  { value: "blacklist", label: "reasons.blacklist", heading: "reasons.blacklistHeading" },
  {
    value: "cancellation",
    label: "reasons.cancellation",
    heading: "reasons.cancellationHeading",
  },
];

const AMENDMENT_COPY: DialogCopy = {
  title: "reasons.amendmentReason",
  fieldLabel: "reasons.reason",
  multiline: true,
};

export const AMENDMENT_GROUPS: GroupDef[] = AMENDMENT_OPTIONS.map(({ value, heading }) => ({
  kind: value,
  heading,
  addLabel: "reasons.addReason",
  copy: AMENDMENT_COPY,
}));

export const ENDORSEMENT_GROUP: GroupDef = {
  kind: "endorsement",
  heading: "reasons.endorsementHeading",
  addLabel: "reasons.addNew",
  copy: {
    title: "reasons.notesReason",
    subtitle: "reasons.notesReason",
    fieldLabel: "reasons.reason",
  },
};

export const COMPLIMENTARY_GROUPS: GroupDef[] = [
  {
    kind: "complimentary_endorsed_by",
    heading: "reasons.endorsedBy",
    addLabel: "reasons.addNew",
    copy: {
      title: "reasons.endorsedByTitle",
      subtitle: "reasons.endorsedByList",
      fieldLabel: "reasons.name",
    },
  },
  {
    kind: "complimentary_approved_by",
    heading: "reasons.approvedBy",
    addLabel: "reasons.addNew",
    copy: {
      title: "reasons.approvedByTitle",
      subtitle: "reasons.approvedByList",
      fieldLabel: "reasons.name",
    },
  },
];

/** The Reasons section as designed: one boxed list per amendment scenario. */
export function ReasonsSection() {
  const t = useTranslation();
  // Which group has the tab, if any. One editor at a time.
  const [editingKind, setEditingKind] = useState<ReasonKind | null>(null);
  return (
    <>
      <div className="pr-5">
        {editingKind === null ? (
          <>
            <h2 className="text-title-2 text-heading">{t("cfg.reasons")}</h2>
            <p className="text-fg-muted text-callout mt-1 mb-6">{t("reasons.intro")}</p>
          </>
        ) : null}
        <div className="divide-line divide-y">
          {AMENDMENT_GROUPS.map((group) => (
            <ReasonGroup
              key={group.kind}
              group={group}
              boxed
              className="py-6 first:pt-0 last:pb-0"
              hidden={editingKind !== null && editingKind !== group.kind}
              onEditingChange={(editing) => setEditingKind(editing ? group.kind : null)}
            />
          ))}
        </div>
      </div>
    </>
  );
}

/** One heading, its add link, and its rows; boxed when the design draws a border. */
export function ReasonGroup({
  group,
  boxed = false,
  className,
  hidden = false,
  onEditingChange,
}: {
  group: GroupDef;
  boxed?: boolean;
  className?: string;
  /** A sibling is being edited, so this one stands aside. */
  hidden?: boolean;
  /** Tells the parent to stand the other groups down while this one edits. */
  onEditingChange?: (editing: boolean) => void;
}) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<Reason | null>(null);
  // Editing one group takes over the tab: the other lists would otherwise sit
  // under the form, offering to add a reason to a different list entirely.
  // Ten at a time; the next ten when the list is scrolled to its end.
  const reasons = useInfiniteQuery({
    queryKey: ["/reasons", group.kind],
    queryFn: ({ pageParam, signal }) =>
      listResource<Reason>(
        "/reasons",
        { kind: group.kind, page: pageParam, size: LIST_PAGE, sort: "id" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const rows = reasons.data?.pages.flatMap((page) => page.items) ?? [];
  // Keyed by kind: the complimentary tab shows two reason lists side by side,
  // and an unqualified parameter would open the form in both.
  const { editing, setEditing, loading } = useRecordParam<Reason>(
    "edit",
    `reason-${group.kind}`,
    "/reasons",
  );
  // Reading the record counts as open: otherwise the lists beside this one stay
  // on screen next to the skeleton, still offering to add rows of their own.
  const editingNow = editing !== null || loading;
  const report = useRef(onEditingChange);
  report.current = onEditingChange;
  useEffect(() => {
    report.current?.(editingNow);
  }, [editingNow]);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["/reasons", group.kind] });
  const remove = useMutation({
    mutationFn: (row: { id: number; updated_at?: string }) =>
      api.delete(`/reasons/${row.id}`, ifMatch(row)),
    onSuccess: () => {
      toast.success(t("common.deleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("common.couldNotDelete"))),
  });

  if (hidden) return null;

  // A record is read when it is opened, so between the click and the answer
  // there is nothing to fill the form with. The same panel, empty, rather than
  // the list sitting there as though the click had missed.
  if (loading) {
    return (
      <section className={className}>
        <FormPanel
          title={t("rp.editSingular").replace("{singular}", t(group.copy.title))}
          onClose={() => setEditing(null)}
          actions={null}
        >
          <Loading busy subject={t(group.copy.title)}>
            <SkeletonLines rows={3} />
          </Loading>
        </FormPanel>
      </section>
    );
  }

  // Adding and editing take over the panel, as they do on every other
  // configuration screen. The kind is not a field: it is the list you opened.
  if (editing !== null) {
    return (
      <section className={className}>
        <EditDialog
          variant="panel"
          singular={t(group.copy.title)}
          endpoint="/reasons"
          fields={[
            {
              name: "reason",
              label: t(group.copy.fieldLabel),
              required: true,
              type: group.copy.multiline ? "textarea" : "text",
            },
          ]}
          fixed={{ kind: group.kind }}
          initial={editing === "new" ? {} : { reason: editing.reason }}
          id={editing === "new" ? null : editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ["/reasons"] });
          }}
        />
      </section>
    );
  }

  return (
    <section className={className}>
      <ListSection
        title={t(group.heading)}
        shown={rows.length}
        total={reasons.data?.pages[0]?.total}
        refreshKeys={[["/reasons", group.kind]]}
        action={
          allow("add__reason") ? (
            <AddButton onClick={() => setEditing("new")}>{t(group.addLabel)}</AddButton>
          ) : undefined
        }
        hasMore={reasons.hasNextPage}
        loadingMore={reasons.isFetchingNextPage}
        pending={reasons.isPending}
        onLoadMore={() => void reasons.fetchNextPage()}
        empty={t("common.nothingYet")}
        listClassName={cn(boxed && "border-line [scrollbar-width:thin] rounded-lg border px-4")}
      >
        {rows.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-fg text-callout">{row.reason}</span>
            <span className="text-subhead flex items-center gap-1">
              {allow("change__reason") ? (
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.edit")}
                </button>
              ) : null}
              {allow("delete__reason") ? (
                <button
                  type="button"
                  onClick={() => setDeleting(row)}
                  className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.delete")}
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ListSection>
      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t(group.copy.title))}
        message={deleting ? t("reasons.confirmDelete").replace("{name}", deleting.reason) : ""}
      />
    </section>
  );
}
